import { Vue, Component, Prop } from 'vue-property-decorator'
import ConfigurationRelationships from '@/modules/configurationModule/shared/components/configuration-relationships/configuration-relationships.vue'
import { ConfigurationSectionResponse } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section/configuration-section.dto'
import {
    ConfigurationLocalAssaysSectionsModalOperationsFormState,
    initialConfigurationLocalAssaysSectionsModalOperationsFormState
} from '@/modules/configurationModule/detail/local-laboratory-module/sections/components/modal-operations-form/configuration-local-assays-sections-modal-operations-form.model'
import { operationService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.service'
import {
    OperationGroupEntities,
    OperationGroupEnum,
    OperationResponse
} from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/operation/operation.dto'
import { commonPortionTypeService } from '@/api/services/common/portion-type/common-portion-type.service'
import { CommonPortionTypeResponse } from '@/api/services/common/portion-type/common-portion-type.dto'
import { commonSubportionTypeService } from '@/api/services/common/subportion-type/common-subportion-type.service'
import { CommonSubportionTypeResponse } from '@/api/services/common/subportion-type/common-subportion-type.dto'
import { configurationSectionOperationService } from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section-operation/configuration-section-operation.service'
import {
    ConfigurationSectionOperationCreateRequest,
    ConfigurationSectionOperationResponse
} from '@/modules/configurationModule/detail/local-laboratory-module/sections/services/configuration-section-operation/configuration-section-operation.dto'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationRelationships
    }
})
export default class ConfigurationLocalAssaysSectionsModalOperationsForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly section!: ConfigurationSectionResponse

    state: ConfigurationLocalAssaysSectionsModalOperationsFormState
    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysSectionsModalOperationsFormState()
    }

    private async _loadSectionsOperations(): Promise<void> {
        this.state.sectionOperations = await this._getSectionsOperations()
    }

    async mounted() {
        await this._loadSectionsOperations()
    }

    private _isOptionAssociated(): boolean {
        return !!this.state.sectionOperations.find(sectionOperation => {
            return sectionOperation.operation.id === this.state.operation?.id &&
                sectionOperation.portionType?.potId === this.state.portionType?.potId &&
                sectionOperation.subportionType?.sptId === this.state.subportionType?.sptId
        })
    }

    private _onOperationComboSelect(): void {
        this.state.subportionType = null
        this.state.portionType = null
    }

    public operationsComboCustomLabel(operation: OperationResponse): string {
        return `${operation.translatedName} - ${OperationGroupEntities[operation.group]}`
    }

    get modelValidated(): boolean {
        return !!this.state.operation?.id && !this._isOptionAssociated()
    }

    get hasSectionOperations(): boolean {
        return !this.state.sectionOperations.length
    }

    get disabledPortionTypeCombo(): boolean {
        return this.state.operation?.group !== OperationGroupEnum.PORTION
    }
    get disabledSubportionTypeCombo(): boolean {
        return this.state.operation?.group !== OperationGroupEnum.SUBPORTION
    }
    /// SERVICES CALLS ///

    async getOperations(query: string): Promise<OperationResponse[]> {
        const filterModel = {
            code: GridUtils.getTextFilterModel('contains', query)
        }

        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(operationService.getAllTaskSectionOperations(filterModel, params))
        if (response) {
            return response.content
        }
        return []
    }

    async getPortionTypes(): Promise<CommonPortionTypeResponse[]> {
        const response = await this.$_toCallUnique(commonPortionTypeService.getPortionTypes())
        if (response) {
            return response
        }
        return []
    }

    async getSubportionTypes(): Promise<CommonSubportionTypeResponse[]> {
        const response = await this.$_toCallUnique(commonSubportionTypeService.getSubportionTypes())
        if (response) {
            return response
        }
        return []
    }

    private async _createSectionOperation(createRequest: ConfigurationSectionOperationCreateRequest): Promise<ConfigurationSectionOperationResponse | null> {
        const response = await this.$_toCallUnique(configurationSectionOperationService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _getSectionsOperations(): Promise<ConfigurationSectionOperationResponse[]> {
        const filterModel: CommonFilterModel = {
            'section.id': GridUtils.getNumberFilterModel('equals', this.section.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationSectionOperationService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _deleteSectionOperation(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(configurationSectionOperationService.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                }
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///

    async createOperation(): Promise<void> {
        if (this.state.operation) {
            const createRequest: ConfigurationSectionOperationCreateRequest = {
                sectionId: this.section.id,
                operationId: this.state.operation.id,
                portionTypeId: this.state.portionType?.potId,
                subportionTypeId: this.state.subportionType?.sptId
            }
            if (await this._createSectionOperation(createRequest)) {
                await this._loadSectionsOperations()
            }
        }
    }

    async deleteOperation(sectionOperation: ConfigurationSectionOperationResponse): Promise<void> {
        if (sectionOperation) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: [sectionOperation.id]
            }
            if (await this._deleteSectionOperation(requestDelete)) {
                await this._loadSectionsOperations()
            }
        }
    }
}
